/**
 * SHELL — central app store: a tiny typed observable holding everything the
 * non-game screens render from. One source of UI truth; screens subscribe and
 * re-render on change.
 */

import type { DateKey } from '@/types';
import type { ScoreMetrics, ScoreSubmissionResult } from '@/types';
import type { AuthIdentity } from '../net/auth';
import type { StreakCache } from '../storage/local';

/** Which non-game screen is showing. The actual run is `phase: 'run'`. */
export type Phase =
  | 'loading'
  | 'landing'
  | 'run'
  | 'results'
  | 'leaderboard'
  | 'streak'
  | 'practice'
  | 'claim';

/** Where the daily run stands for this device today. */
export type DailyStatus = 'unplayed' | 'in_progress' | 'completed';

export interface RunResult {
  readonly metrics: ScoreMetrics;
  readonly submission?: ScoreSubmissionResult;
  /** True once a transport-successful submit happened (accepted or rejected). */
  readonly synced: boolean;
}

export interface AppSnapshot {
  readonly phase: Phase;
  readonly dateKey: DateKey;
  readonly seed: number;
  readonly identity: AuthIdentity;
  readonly streak: StreakCache;
  readonly dailyStatus: DailyStatus;
  readonly result: RunResult | null;
  /** True when a course is ready and we can start a run. */
  readonly courseReady: boolean;
  /** Whether we're operating with no real backend session. */
  readonly offline: boolean;
}

export type Listener = (snap: AppSnapshot) => void;

export interface Store {
  get(): AppSnapshot;
  set(patch: Partial<AppSnapshot>): void;
  subscribe(listener: Listener): () => void;
}

export function createStore(initial: AppSnapshot): Store {
  let snapshot = initial;
  const listeners = new Set<Listener>();

  return {
    get: () => snapshot,
    set(patch) {
      snapshot = { ...snapshot, ...patch };
      for (const l of listeners) l(snapshot);
    },
    subscribe(listener) {
      listeners.add(listener);
      listener(snapshot);
      return () => listeners.delete(listener);
    },
  };
}
